import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Lock, Camera, Save, Package, Heart, LogOut, Edit2 } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const Profile = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        firstName: 'Priya',
        lastName: 'Sharma',
        email: 'priya@email.com',
        phone: '+91 98765 43210',
        address: '42 MG Road, Indiranagar',
        city: 'Bangalore',
        state: 'Karnataka',
        pincode: '560038',
    });

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = () => {
        devLog('Profile saved:', formData);
        setIsEditing(false);
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'orders', label: 'Orders', icon: Package },
        { id: 'wishlist', label: 'Wishlist', icon: Heart },
    ];

    const inputClass = "w-full px-4 py-3 text-sm border border-[#E2E8F0] rounded-none focus:outline-none focus:border-[#2563EB] bg-white text-[#0F172A] placeholder-[#94A3B8]";
    const readonlyClass = "w-full px-4 py-3 text-sm border border-[#E2E8F0] rounded-none bg-[#F8FAFC] text-[#0F172A]";

    return (
        <div className="w-full min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div className="pt-20 md:pt-24 pb-8 bg-white border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Account</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">My Profile</h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white border border-[#E2E8F0] rounded-none p-6 relative">
                            <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                            {/* Avatar */}
                            <div className="flex flex-col items-center mb-6">
                                <div className="relative w-20 h-20 bg-[#F8FAFC] border border-[#E2E8F0] rounded-none flex items-center justify-center mb-3">
                                    <User size={32} className="text-[#64748B]" />
                                    <button className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#2563EB] flex items-center justify-center rounded-none">
                                        <Camera size={12} className="text-white" />
                                    </button>
                                </div>
                                <p className="text-sm font-semibold text-[#0F172A]">{formData.firstName} {formData.lastName}</p>
                                <p className="text-xs text-[#64748B] font-light">{formData.email}</p>
                            </div>

                            {/* Tabs */}
                            <nav className="space-y-1">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => tab.id === 'orders' ? navigate('/template9/orders') : setActiveTab(tab.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-none transition-colors duration-300 bg-transparent border-none cursor-pointer text-left ${
                                            activeTab === tab.id
                                                ? 'bg-[#F8FAFC] text-[#2563EB] border-l-2 border-l-[#2563EB]'
                                                : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                                        }`}
                                    >
                                        <tab.icon size={16} />
                                        {tab.label}
                                    </button>
                                ))}
                                <button
                                    onClick={() => navigate('/template9/login')}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50 rounded-none bg-transparent border-none cursor-pointer text-left transition-colors duration-300"
                                >
                                    <LogOut size={16} />
                                    Sign Out
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-3">
                        <div className="bg-white border border-[#E2E8F0] rounded-none p-6 sm:p-8">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-lg font-semibold text-[#0F172A] uppercase tracking-wider">Personal Information</h2>
                                    <div className="w-8 h-0.5 bg-[#2563EB] mt-2" />
                                </div>
                                <Button
                                    variant={isEditing ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={isEditing ? handleSave : () => setIsEditing(true)}
                                    icon={isEditing ? Save : Edit2}
                                >
                                    {isEditing ? 'Save' : 'Edit'}
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">First Name</label>
                                    <input
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">Last Name</label>
                                    <input
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">Email</label>
                                    <input
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">Phone</label>
                                    <input
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">Address</label>
                                    <input
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">City</label>
                                    <input
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">State</label>
                                    <input
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-[#0F172A] uppercase tracking-wider mb-2">PIN Code</label>
                                    <input
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleChange}
                                        readOnly={!isEditing}
                                        className={isEditing ? inputClass : readonlyClass}
                                    />
                                </div>
                            </div>

                            {/* Change Password Section */}
                            <div className="mt-10 pt-8 border-t border-[#E2E8F0]">
                                <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider mb-4">Security</h3>
                                <div className="w-6 h-0.5 bg-[#2563EB] mb-4" />
                                <Button variant="secondary" size="sm" icon={Lock}>
                                    Change Password
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Profile;
